import { createFeature, createReducer, createSelector, on, select } from '@ngrx/store';
import { UserActions } from '@src/state';
import { StoreFeatures } from '@state/store.utils';
import { MsisdnInfo, ProductType, UserResponse } from '@core/contracts/data-contracts';
import { ActionStatus } from '@core/models';
import { BlockedBy } from '@core/enums/blocked-by.enum';
import { ACTIVE_PRODUCT_LOCALSTORAGE_KEY, CURRENT_MSISDN_LOCALSTORAGE_KEY } from '@core/const/const';
import { PortingBlockingCode } from '@core/enums/porting-blocking-code.enum';

export type UserState = {
  current: UserResponse | null;
  isLoading: boolean;

  emailRequestStatus: ActionStatus;
  passwordRequestStatus: ActionStatus;
  userUpdateRequestStatus: ActionStatus;

  selectedMsisdn: MsisdnInfo;
  msisdns: MsisdnInfo[];

  products: ProductType[];
  activeProduct: ProductType;

  limitations: {
    currentMsisdnIsLimited: boolean;

    eligibleForProlongation: boolean;
    contractDate: string;

    isRunningOutOnData: boolean;
    dataUsagePercentage: number;
  };
};

export const initialUserState: UserState = {
  current: null,
  isLoading: false,

  emailRequestStatus: ActionStatus.Initial,
  passwordRequestStatus: ActionStatus.Initial,
  userUpdateRequestStatus: ActionStatus.Initial,

  selectedMsisdn: null,
  msisdns: [],
  products: [],
  activeProduct:
    (localStorage.getItem(ACTIVE_PRODUCT_LOCALSTORAGE_KEY) as ProductType) ?? ProductType.Mobile, // Temporary default until the backend returns user's product list

  // flags for edge cases
  limitations: {
    currentMsisdnIsLimited: false,

    eligibleForProlongation: false,
    contractDate: null,

    isRunningOutOnData: false,
    dataUsagePercentage: 0,
  },
};

// TODO: remove once the backend returns the user's actual product list 
const INTERNET_MOCK_MSISDN = '31657507590';

export const userReducer = createReducer(
  initialUserState,
  on(UserActions.getUserDetails, (state): UserState => ({
    ...state,
    isLoading: true,
  })),
  on(UserActions.getUserDetailsSuccess, (state, { user }): UserState => {
    const hasInternetMock = user.msisdns?.some((m) => m.msisdn === INTERNET_MOCK_MSISDN);

    return {
      ...state,
      current: user,
      selectedMsisdn: user.msisdns[0],
      msisdns: user.msisdns,
      products: hasInternetMock ? [ProductType.Mobile, ProductType.Internet] : [ProductType.Mobile],
      isLoading: false,

      limitations: state.limitations,
    };
  }),
  on(UserActions.getUserDetailsFailure, (state): UserState => ({
    ...state,
    isLoading: false,
  })),
  on(UserActions.clearUserDetails, (state): UserState => ({
    ...state,
    current: null,
    isLoading: false,
  })),
  on(UserActions.setActiveProduct, (state, { product }): UserState => {
    localStorage.setItem(ACTIVE_PRODUCT_LOCALSTORAGE_KEY, product);

    return {
      ...state,
      activeProduct: product,
      isLoading: false,
    };
  }),
  on(UserActions.changeEmailSuccess, (state, { email }): UserState => ({
    ...state,
    emailRequestStatus: ActionStatus.Success,
  })),
  on(UserActions.changeEmailFailure, (state): UserState => ({
    ...state,
    emailRequestStatus: ActionStatus.Failed,
  })),
  on(UserActions.clearEmailRequestStatus, (state): UserState => ({
    ...state,
    emailRequestStatus: ActionStatus.Initial,
  })),
  on(UserActions.changePasswordSuccess, (state): UserState => ({
    ...state,
    passwordRequestStatus: ActionStatus.Success,
  })),
  on(UserActions.changePasswordFailure, (state): UserState => ({
    ...state,
    passwordRequestStatus: ActionStatus.Failed,
  })),
  on(UserActions.clearPasswordRequestStatus, (state): UserState => ({
    ...state,
    passwordRequestStatus: ActionStatus.Initial,
  })),
  on(UserActions.updateUserSuccess, (state, { payload }): UserState => ({
    ...state,
    current: {
      ...state.current,
      ...payload,
    },
    userUpdateRequestStatus: ActionStatus.Success,
  })),
  on(UserActions.updateUserFailure, (state): UserState => ({
    ...state,
    userUpdateRequestStatus: ActionStatus.Failed,
  })),
  on(UserActions.clearUpdateUserRequestStatus, (state): UserState => ({
    ...state,
    userUpdateRequestStatus: ActionStatus.Initial,
  })),

  on(UserActions.changeCurrentMsisdn, (state, { customerId }): UserState => {
    const selectedMsisdn = state.msisdns.find((m) => m.customerId === customerId);

    if (selectedMsisdn) {
      localStorage.setItem(CURRENT_MSISDN_LOCALSTORAGE_KEY, JSON.stringify({ customerId }));
    }

    return {
      ...state,
      selectedMsisdn: { ...selectedMsisdn },
      limitations: state.limitations,
    };
  }),
  on(UserActions.signOut, (): UserState => initialUserState)
);

export const userFeature = createFeature({
  name: StoreFeatures.User,
  reducer: userReducer,
  extraSelectors: ({
    selectCurrent,
    selectSelectedMsisdn,
    selectLimitations,
    selectActiveProduct,
  }) => ({
    selectSelectedMsisdnNumber: createSelector(selectSelectedMsisdn, (query) => query?.msisdn),
    selectCustomerId: createSelector(selectSelectedMsisdn, (query) => query?.customerId),
    selectUserInitials: createSelector(selectSelectedMsisdn, (msisdn) => {
      const firstInitial = msisdn?.firstName?.[0];
      const lastInitial = msisdn?.lastName?.[0];
      return firstInitial && lastInitial ? `${firstInitial}.${lastInitial}.` : '';
    }),
    selectUserNameDetails: createSelector(selectSelectedMsisdn, (msisdn) => ({
      firstName: msisdn?.firstName,
      lastName: msisdn?.lastName,
      prefix: msisdn?.prefix ?? '',
    })),
    selectUserFirstName: createSelector(selectSelectedMsisdn, (msisdn) => msisdn?.firstName),
    selectIsMsisdnLimited: createSelector(
      selectSelectedMsisdn,
      (msisdn: MsisdnInfo) => msisdn?.isLimited ?? false
    ),
    selectIsMsisdnActive: createSelector(
      selectSelectedMsisdn,
      (msisdn) =>
        msisdn?.status?.isActivated &&
        !msisdn?.status?.isBlocked &&
        !msisdn?.status?.isSwapPending &&
        !msisdn?.isTerminated
    ),
    selectIsEligibleForProlongation: createSelector(selectLimitations, (limitations) => ({
      eligibleForProlongation: limitations.eligibleForProlongation,
      contractDate: limitations.contractDate,
    })),
    selectRunningOutOfUsage: createSelector(selectLimitations, (limitations) => ({
      isRunningOutOnData: limitations.isRunningOutOnData,
      dataUsagePercentage: limitations.dataUsagePercentage,
    })),
    selectMsisdnStatus: createSelector(selectSelectedMsisdn, (msisdn: MsisdnInfo) => {
      const { status } = msisdn;
      return status;
    }),
    selectIsActivationPending: createSelector(
      selectSelectedMsisdn,
      (msisdn: MsisdnInfo) => msisdn?.status?.isActivationPending ?? false
    ),
    selectIsActivationProcessPending: createSelector(
      selectSelectedMsisdn,
      (msisdn: MsisdnInfo) => msisdn?.status?.isActivationProcessPending ?? false
    ),
    selectIsMsisdnBlocked: createSelector(
      selectSelectedMsisdn,
      (msisdn: MsisdnInfo) => msisdn?.status?.isBlocked ?? false
    ),
    selectIsSwapPending: createSelector(
      selectSelectedMsisdn,
      (msisdn: MsisdnInfo) => msisdn?.status?.isSwapPending ?? false
    ),
    selectIsPendingPayment: createSelector(
      selectSelectedMsisdn,
      (msisdn: MsisdnInfo) => msisdn?.status?.isPendingPayment ?? false
    ),
    selectIsBlockedByCrm: createSelector(selectSelectedMsisdn, (msisdn: MsisdnInfo) =>
      msisdn ? msisdn.status.blockedBy === BlockedBy.CrmHardBlock : false
    ),
    selectIsSoftBlocked: createSelector(selectSelectedMsisdn, (msisdn: MsisdnInfo) =>
      msisdn ? msisdn.status.blockedBy === BlockedBy.CrmSoftBlock : false
    ),
    selectIsBlockedByUser: createSelector(
      selectSelectedMsisdn,
      (msisdn: MsisdnInfo) => msisdn && msisdn.status?.blockedBy === BlockedBy.MyTelecomHardBlock
    ),
    selectIsJottaInUse: createSelector(
      selectSelectedMsisdn,
      (msisdn: MsisdnInfo) => msisdn.isJottaInUse ?? false
    ),
    selectIsBlockPending: createSelector(
      selectSelectedMsisdn,
      (msisdn: MsisdnInfo) => msisdn?.status?.isBlockPending ?? false
    ),
    selectIsUnblockPending: createSelector(
      selectSelectedMsisdn,
      (msisdn: MsisdnInfo) => msisdn?.status?.isUnblockPending ?? false
    ),
    selectIsActivated: createSelector(
      selectSelectedMsisdn,
      (msisdn: MsisdnInfo) => msisdn?.status?.isActivated ?? false
    ),
    selectCanBeBlockResumed: createSelector(
      selectSelectedMsisdn,
      (msisdn: MsisdnInfo) => msisdn?.status?.canBeBlockResumed ?? false
    ),
    selectHasPortingForActive: createSelector(
      selectSelectedMsisdn,
      (msisdn: MsisdnInfo) => msisdn?.status?.hasPortingForActive ?? false
    ),
    selectIsTerminated: createSelector(
      selectSelectedMsisdn,
      (msisdn: MsisdnInfo) => msisdn?.isTerminated ?? false
    ),
    selectContractEndDate: createSelector(
      selectSelectedMsisdn,
      (msisdn: MsisdnInfo) => msisdn?.contractEndDate
    ),
    selectSwapType: createSelector(selectSelectedMsisdn, (msisdn: MsisdnInfo) => msisdn?.swapType),
    selectIsEmployee: createSelector(
      selectSelectedMsisdn,
      (msisdn: MsisdnInfo) => msisdn?.isEmployee
    ),
    selectIsB2B: createSelector(selectCurrent, (current: UserResponse) => current?.isB2b),

    selectIsPortingBlocked: createSelector(
      selectSelectedMsisdn,
      (msisdn: MsisdnInfo) =>
        msisdn?.status?.portingBlockingCode === PortingBlockingCode.OutsidePortingWindow
    ),
    // TODO: add installation pending check from API once installation status is available
    selectShowInstallationStatus: createSelector(
      selectActiveProduct,
      (activeProduct) => activeProduct === ProductType.Internet
    ),
  }),
});
